import { Leaf, Target, Eye, Award, BookOpen, ShieldPlus } from "lucide-react";
import aboutImg from "../assets/about-farm.jpg";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";

const About = () => {
  const [about, setAbout] = useState(null);
  const { i18n } = useTranslation();
  const lang = i18n.language; // "en" or "ar"

  useEffect(() => {
    const load = async () => {
      const res = await api.get("/about-us");
      setAbout(res.data);
    };
    load();
  }, []);

  if (!about) return null;

  const values = [
    {
      icon: ShieldPlus,
      title: about[`value1_title_${lang}`],
      description: about[`value1_description_${lang}`],
    },
    {
      icon: Award,
      title: about[`value2_title_${lang}`],
      description: about[`value2_description_${lang}`],
    },
    {
      icon: BookOpen,
      title: about[`value3_title_${lang}`],
      description: about[`value3_description_${lang}`],
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={aboutImg}
            alt="Herbal farm at sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 overlay-green" />
        </div>
        <div className="relative z-10 container-custom text-center text-cream px-4">
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4 animate-fade-in">
            {about[`hero_title_${lang}`]}
          </h1>
          <p
            className="text-xl text-cream/90 max-w-2xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            {about[`hero_subtitle_${lang}`]}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                {about[`story_title_${lang}`]}
              </h2>
              <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                <p>{about[`story_paragraph_1_${lang}`]}</p>
                <p>{about[`story_paragraph_2_${lang}`]}</p>
                <p>{about[`story_paragraph_3_${lang}`]}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-secondary">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-card p-10 rounded-3xl shadow-soft animate-slide-up">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-foreground mb-4">
                {lang === "ar" ? "مهمتنا" : "Our Mission"}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {about[`mission_text_${lang}`]}
              </p>
            </div>

            {/* Vision */}
            <div
              className="bg-card p-10 rounded-3xl shadow-soft animate-slide-up"
              style={{ animationDelay: "0.15s" }}
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-foreground mb-4">
                {lang === "ar" ? "رؤيتنا" : "Our Vision"}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {about[`vision_text_${lang}`]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              {lang === "ar" ? "قيمنا" : "What We Stand For"}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {lang === "ar"
                ? "قيمنا توجه كل ما نقوم به من المصدر إلى التسليم."
                : "Our core values guide everything we do, from sourcing to delivery."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="benefit-card animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground mb-3">
                  {value.title}
                </h3>
                <p className="text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emphasis Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto">
            <Leaf className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
              {about[`emphasis_title_${lang}`]}
            </h2>
            <p className="text-primary-foreground/80 text-lg leading-relaxed">
              {about[`emphasis_text_${lang}`]}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
